import { SB, createMessage } from '@play-co/replicant';
import createMessages from '../config/messages/createMessages';
import {
  cardsAddCardReceived,
  cardsAddIncomingTrade,
  cardsApplyTradeCounter,
  cardsCancelTrade,
  cardsCompleteTradeAsRecipient,
  cardsCreateCounteredShareTradeAsProposer,
  cardsSendCard,
} from './cards.modifiers';
import { CardId } from './cards.ruleset';
import {
  cardsCanSendCard,
  cardsGetOwnedCardCount,
  cardsGetPendingTrade,
  cardsIsCardValid,
} from './cards.getters';

const cardPendingTradeMessageSchema = SB.object({
  tradeId: SB.string(),
  proposerId: SB.string(),
  recipientId: SB.string(),
  otherUserName: SB.string().optional(),
  otherUserAvatar: SB.string().optional(),
  offeredCardIds: SB.array(SB.string()),
  counterCardIds: SB.array(SB.string()).optional(),
  status: SB.tuple(['proposed', 'countered'] as const),
  createdAt: SB.int(),
  updatedAt: SB.int(),
  recipientConfirmedAt: SB.int().optional(),
  proposerConfirmedAt: SB.int().optional(),
});

export const cardsMessages = createMessages({
  cardsSendCard: createMessage(
    SB.object({ cardId: SB.string() }),
    (state, args, meta) => {
      cardsAddCardReceived(state, {
        userId: meta.senderId,
        cardId: args.cardId as CardId,
        now: meta.timestamp,
      });
    },
  ),

  cardsDonateShareClaimed: createMessage(
    SB.object({
      cardId: SB.string(),
    }),
    (state, args, meta) => {
      if (!cardsIsCardValid(args.cardId)) return;
      if (!cardsCanSendCard(state, meta.timestamp)) return;
      if (cardsGetOwnedCardCount(state, args.cardId, meta.timestamp) < 2) {
        return;
      }

      cardsSendCard(state, {
        cardId: args.cardId,
        now: meta.timestamp,
      });
    },
  ),

  cardsTradeShareCountered: createMessage(
    SB.object({
      tradeId: SB.string(),
      offeredCardId: SB.string(),
      counterCardId: SB.string(),
      recipientName: SB.string().optional(),
      recipientAvatar: SB.string().optional(),
    }),
    (state, args, meta) => {
      if (!cardsIsCardValid(args.offeredCardId)) return;
      if (!cardsIsCardValid(args.counterCardId)) return;

      cardsCreateCounteredShareTradeAsProposer(state, {
        tradeId: args.tradeId,
        recipientId: meta.senderId,
        otherUserName: args.recipientName,
        otherUserAvatar: args.recipientAvatar,
        offeredCardId: args.offeredCardId,
        counterCardId: args.counterCardId,
        now: meta.timestamp,
      });
    },
  ),

  cardsTradeProposed: createMessage(
    SB.object({ trade: cardPendingTradeMessageSchema }),
    (state, args, meta) => {
      if (args.trade.recipientId !== state.id) return;
      if (args.trade.proposerId !== meta.senderId) return;
      if (args.trade.status !== 'proposed') return;
      if (args.trade.offeredCardIds.length !== 1) return;
      if (!cardsIsCardValid(args.trade.offeredCardIds[0])) return;
      if (args.trade.counterCardIds !== undefined) return;

      cardsAddIncomingTrade(state, args.trade, meta.timestamp);
    },
  ),

  cardsTradeCountered: createMessage(
    SB.object({
      tradeId: SB.string(),
      counterCardIds: SB.array(SB.string()),
      recipientConfirmedAt: SB.int(),
      updatedAt: SB.int(),
    }),
    (state, args, meta) => {
      const trade = cardsGetPendingTrade(state, args.tradeId, meta.timestamp);
      if (!trade || trade.proposerId !== state.id) return;
      if (trade.recipientId !== meta.senderId) return;
      if (args.counterCardIds.length !== 1) return;

      const counterCardId = args.counterCardIds[0];
      if (!cardsIsCardValid(counterCardId)) return;

      cardsApplyTradeCounter(state, {
        tradeId: args.tradeId,
        counterCardIds: [counterCardId],
        recipientConfirmedAt: args.recipientConfirmedAt,
        updatedAt: args.updatedAt,
        now: meta.timestamp,
      });
    },
  ),

  cardsTradeCompleted: createMessage(
    SB.object({
      tradeId: SB.string(),
      completedAt: SB.int(),
    }),
    (state, args, meta) => {
      const trade = cardsGetPendingTrade(state, args.tradeId, args.completedAt);
      if (!trade || trade.recipientId !== state.id) return;
      if (trade.proposerId !== meta.senderId) return;

      cardsCompleteTradeAsRecipient(state, args.tradeId, args.completedAt);
    },
  ),

  cardsTradeCancelled: createMessage(
    SB.object({
      tradeId: SB.string(),
    }),
    (state, args, meta) => {
      const trade = state.cards?.cardTrades?.pendingTrades?.[args.tradeId];
      if (!trade) return;
      if (
        trade.proposerId !== meta.senderId &&
        trade.recipientId !== meta.senderId
      ) {
        return;
      }

      cardsCancelTrade(state, args.tradeId);
    },
  ),
});
